/**
 * NavigationFullScreen — an IN-APP, full-screen live navigation map for the
 * driver, with the compact LIVE fare overlay on top of it.
 *
 * WHY THIS EXISTS
 * The "Navigate" button hands the driver off to the EXTERNAL Google Maps / Waze
 * app, and a web app cannot draw an overlay on top of another native app. So the
 * ONLY way to show the live fare *while the driver is navigating* is to let them
 * navigate INSIDE Crystal Dispatch. This screen is that in-app navigation view:
 * a full-screen map that follows the driver's live GPS along the route, with a
 * heading banner (destination + remaining distance) and the fare pill overlaid.
 *
 * The driver never leaves this screen to see the fare — tap the pill to expand
 * the full FareMeterPanel over the map, tap ✕ to collapse back to the pill.
 *
 * SCOPE: frontend/UI only. Reuses <SmartMap/> (route + live markers) and the
 * read-only /driver/live-fare poll via <FloatingFarePill/>. No fare-calc,
 * dispatch, lifecycle, offer-routing, janitor or state-machine changes. A
 * secondary "Open in Google Maps / Waze" button is kept for drivers who want
 * native voice guidance.
 */
import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  X, ExternalLink, Navigation2, CornerUpLeft, CornerUpRight, ArrowUp,
  RotateCw, ArrowUpLeft, ArrowUpRight, Merge, Compass,
  Volume2, VolumeX, Gauge, Clock, AlertTriangle, Loader2,
  Phone, MessageSquare, Send, Bug,
} from "lucide-react";
import { toast } from "sonner";

import { SmartMap } from "../SmartMap";
import { FloatingFarePill } from "./FloatingFarePill";
import { useNavVoice, primeVoice } from "../../hooks/useNavVoice";
import { useTurnByTurn } from "../../hooks/useTurnByTurn";
import { api } from "../../lib/api";
import { DRIVER_TEMPLATES } from "../../lib/rideMessageTemplates";
import { BUILD_TAG } from "../../lib/buildTag";

// ── Bearing helpers (great-circle initial bearing, degrees from true north) ──
const _toRad = (d) => (d * Math.PI) / 180;
const _toDeg = (r) => (r * 180) / Math.PI;
const _bearing = (a, b) => {
  const φ1 = _toRad(a.lat), φ2 = _toRad(b.lat), Δλ = _toRad(b.lng - a.lng);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (_toDeg(Math.atan2(y, x)) + 360) % 360;
};
const _haversineKm = (a, b) => {
  const R = 6371, dLat = _toRad(b.lat - a.lat), dLng = _toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 +
    Math.cos(_toRad(a.lat)) * Math.cos(_toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
};

// Shortest distance (metres) from a point to a polyline, using point-to-SEGMENT
// distance (not just vertices) via a local equirectangular projection. The
// overview polyline is decimated, so vertex-only distance massively over-reports
// and triggers false "off-route" — segment distance is accurate.
const _M_PER_DEG = 111320;
const _xy = (p, ref) => ({
  x: (p.lng - ref.lng) * Math.cos(_toRad(ref.lat)) * _M_PER_DEG,
  y: (p.lat - ref.lat) * _M_PER_DEG,
});
const _distToPolylineM = (p, pts) => {
  if (!p || !Array.isArray(pts) || pts.length === 0) return Infinity;
  if (pts.length === 1) return _haversineKm(p, pts[0]) * 1000;
  let min = Infinity;
  for (let i = 0; i < pts.length - 1; i++) {
    const ref = pts[i];
    const P = _xy(p, ref), A = { x: 0, y: 0 }, B = _xy(pts[i + 1], ref);
    const dx = B.x - A.x, dy = B.y - A.y;
    const len2 = dx * dx + dy * dy;
    let t = len2 > 0 ? ((P.x - A.x) * dx + (P.y - A.y) * dy) / len2 : 0;
    t = Math.max(0, Math.min(1, t));
    const cx = A.x + t * dx, cy = A.y + t * dy;
    const d = Math.hypot(P.x - cx, P.y - cy);
    if (d < min) min = d;
  }
  return min;
};

// Snap a point to the nearest position ON the route polyline (the drawn line),
// returning { point, distM }. Used to glue the displayed vehicle marker to the
// route like Google Maps. Raw GPS is preserved for progress/diagnostics.
const _snapToPolyline = (p, pts) => {
  if (!p || !Array.isArray(pts) || pts.length < 2) return null;
  let best = null;
  let min = Infinity;
  for (let i = 0; i < pts.length - 1; i++) {
    const ref = pts[i];
    const P = _xy(p, ref);
    const B = _xy(pts[i + 1], ref);
    const len2 = B.x * B.x + B.y * B.y;
    let t = len2 > 0 ? (P.x * B.x + P.y * B.y) / len2 : 0;
    t = Math.max(0, Math.min(1, t));
    const d = Math.hypot(P.x - t * B.x, P.y - t * B.y);
    if (d < min) {
      min = d;
      best = {
        lat: pts[i].lat + (pts[i + 1].lat - pts[i].lat) * t,
        lng: pts[i].lng + (pts[i + 1].lng - pts[i].lng) * t,
      };
    }
  }
  return best ? { point: best, distM: min } : null;
};

// Derive a course heading from the route when the device gives no GPS heading:
// find the route vertex nearest the driver, then take the bearing to a vertex a
// few steps ahead (falls back to the destination marker).
const _routeBearing = (driver, route, dropoff) => {
  if (driver && route && route.length > 1) {
    let nearest = 0, best = Infinity;
    for (let i = 0; i < route.length; i++) {
      const d = _haversineKm(driver, route[i]);
      if (d < best) { best = d; nearest = i; }
    }
    const ahead = route[Math.min(nearest + 3, route.length - 1)];
    if (ahead && (ahead.lat !== driver.lat || ahead.lng !== driver.lng)) {
      return _bearing(driver, ahead);
    }
  }
  if (driver && dropoff && dropoff.lat != null) return _bearing(driver, dropoff);
  return null;
};

// Map Google Directions `maneuver` codes → a glanceable icon.
const MANEUVER_ICON = {
  "turn-left": CornerUpLeft,
  "turn-right": CornerUpRight,
  "turn-slight-left": ArrowUpLeft,
  "turn-slight-right": ArrowUpRight,
  "turn-sharp-left": CornerUpLeft,
  "turn-sharp-right": CornerUpRight,
  "uturn-left": RotateCw,
  "uturn-right": RotateCw,
  "roundabout-left": RotateCw,
  "roundabout-right": RotateCw,
  "fork-left": ArrowUpLeft,
  "fork-right": ArrowUpRight,
  merge: Merge,
  straight: ArrowUp,
  "ramp-left": ArrowUpLeft,
  "ramp-right": ArrowUpRight,
};

// Strip the HTML Google returns in step instructions → plain text.
const plainText = (html) =>
  typeof html === "string" ? html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim() : "";

// Build tag — shown in the nav header diagnostic so a real-device tester can
// confirm at a glance whether the latest navigation code is actually loaded
// (vs. a stale cached PWA bundle, which has previously masked deployed fixes).
const NAV_BUILD = BUILD_TAG;

// Parse the current/target road name from a Google step instruction.
const roadFromInstruction = (instruction) => {
  const t = plainText(instruction);
  if (!t) return null;
  let m = t.match(/ onto (.+?)(?: toward| for |$)/i);
  if (m) return m[1].trim();
  m = t.match(/ on (.+?)(?: toward| for |$)/i);
  if (m) return m[1].trim();
  return null;
};

export function NavigationFullScreen({
  open,
  onClose,
  markers,
  route,
  rideId,
  startedAt,
  fallbackEstimate,
  destLabel,
  distanceText,
  nextStep,
  steps,
  etaText,
  navUrl,
  navAppLabel = "Google Maps",
  gpsHeading = null,
  gpsSpeedMph = null,
  etaSeconds = null,
  remainingMeters = null,
  onReroute,
  phoneBooking = false,
  gpsAccuracy = null,
  lastFixAt = null,
  lastHeartbeatAt = null,
}) {
  // Orientation: false = North Up (bearing 0°), true = Head Up (follow heading).
  // Hooks must run unconditionally — declared before the `open` early return.
  const [headUp, setHeadUp] = useState(true);
  // Voice guidance — default OFF; the toggle tap doubles as the audio user
  // gesture mobile browsers require.
  const [voiceOn, setVoiceOn] = useState(false);
  // Navigation debug overlay (req #10) — off by default.
  const [debugOn, setDebugOn] = useState(false);
  // Quick-message sheet (templated, reuses the existing send endpoints).
  const [msgOpen, setMsgOpen] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  // Diagnostic: live map rendering type (VECTOR required for Head-Up rotation).
  const [renderingType, setRenderingType] = useState(null);

  // Live driver position from the marker set.
  const driverPos = useMemo(() => {
    const m = (markers || []).find(
      (mk) => (mk.type === "driver" || mk.type === "me") && mk.lat != null && mk.lng != null,
    );
    return m ? { lat: m.lat, lng: m.lng } : null;
  }, [markers]);

  // Movement-derived bearing: the most reliable "direction of travel" signal,
  // computed from how the live GPS position actually moves between fixes. Many
  // phones report `coords.heading` as null unless moving fast / compass-enabled,
  // so deriving it from consecutive positions makes the marker point correctly
  // even when the device gives no heading. Updated only after ~8 m of travel so
  // GPS jitter while stationary doesn't spin the marker.
  const [moveHeading, setMoveHeading] = useState(null);
  const lastHeadingPosRef = useRef(null);
  useEffect(() => {
    if (!driverPos) return;
    const prev = lastHeadingPosRef.current;
    if (!prev) { lastHeadingPosRef.current = driverPos; return; }
    if (_haversineKm(prev, driverPos) * 1000 >= 8) {
      setMoveHeading(_bearing(prev, driverPos));
      lastHeadingPosRef.current = driverPos;
    }
  }, [driverPos]);

  // Client-side turn-by-turn: advances through the maneuvers from live GPS so
  // the banner/voice update every tick rather than every 20s poll.
  const tbt = useTurnByTurn(steps, driverPos);
  const activeStep = tbt.step || nextStep || null;
  const arrived = !!tbt.arrived;
  const maneuverMeters = Number.isFinite(tbt.distanceM)
    ? tbt.distanceM
    : (Number.isFinite(activeStep?.distance_meters) ? activeStep.distance_meters : null);
  const maneuverDistanceText = tbt.distanceText || activeStep?.distance_text || null;
  // Heading provenance for the debug overlay.
  const headingSource = Number.isFinite(moveHeading)
    ? "movement"
    : Number.isFinite(gpsHeading) ? "gps-course" : "route-derived";

  // Resolve the heading to rotate to / point the marker:
  //   1) the movement-derived bearing (direction the vehicle is actually going),
  //   2) the device GPS heading/course when available, else
  //   3) a course bearing derived from the route geometry, else
  //   4) null → graceful "heading unavailable" note (no rotation, no failure).
  const headingDeg = useMemo(() => {
    if (Number.isFinite(moveHeading)) return moveHeading;
    if (Number.isFinite(gpsHeading)) return gpsHeading;
    const list = markers || [];
    const driver = list.find(
      (m) => (m.type === "driver" || m.type === "me") && m.lat != null && m.lng != null,
    );
    const dropoff = list.find((m) => m.type === "dropoff" && m.lat != null);
    return _routeBearing(driver, route, dropoff);
  }, [moveHeading, gpsHeading, markers, route]);

  // Off-route detection: shortest distance from the live driver position to the
  // route polyline (point-to-segment). > ~70 m ⇒ off-route. The threshold allows
  // for GPS noise + the gap between the GPS fix and Google's road-snapped route.
  const OFF_ROUTE_M = 70;
  const offRoute = useMemo(() => {
    const driver = (markers || []).find(
      (m) => (m.type === "driver" || m.type === "me") && m.lat != null && m.lng != null,
    );
    if (!driver || !route || route.length < 2) return false;
    return _distToPolylineM({ lat: driver.lat, lng: driver.lng }, route) > OFF_ROUTE_M;
  }, [markers, route]);

  // Trigger an immediate reroute (at most once per 10s) while off-route. The
  // 20s directions poll also self-heals from live GPS; this makes it instant.
  const lastRerouteRef = useRef(0);
  useEffect(() => {
    if (!offRoute) return;
    const now = Date.now();
    if (now - lastRerouteRef.current > 10000) {
      lastRerouteRef.current = now;
      onReroute?.();
    }
  }, [offRoute, onReroute]);

  // Spoken prompts (no-op until enabled) — distance-threshold + arrival driven.
  const voice = useNavVoice(voiceOn && open && !offRoute, activeStep, maneuverMeters, arrived);

  if (!open) return null;

  const ManeuverIcon = (activeStep && MANEUVER_ICON[activeStep.maneuver]) || Navigation2;
  const instruction = plainText(activeStep?.instruction);
  const headUpUnavailable = headUp && headingDeg == null;
  // Upcoming-after-next maneuver → "then …" preview line (req #5).
  const thenStep = tbt.thenStep || null;
  const ThenIcon = (thenStep && MANEUVER_ICON[thenStep.maneuver]) || Navigation2;
  const thenInstruction = plainText(thenStep?.instruction);

  // Near-turn detection: within ~100 ft (30 m) of the upcoming maneuver, using
  // the live client-computed distance so it updates between polls.
  const NEAR_TURN_METERS = 30;
  const nearMeters = Number.isFinite(maneuverMeters) ? maneuverMeters : null;
  const nearTurn = !!instruction && !offRoute && nearMeters != null && nearMeters <= NEAR_TURN_METERS;

  // ── Derived HUD values (all from navigation route data / GPS) ──────────────
  const remainingSecs = Number.isFinite(etaSeconds) ? etaSeconds : null;
  const etaClock = remainingSecs != null
    ? new Date(Date.now() + remainingSecs * 1000)
        .toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false })
    : null;
  const arriveMin = remainingSecs != null ? Math.max(1, Math.round(remainingSecs / 60)) : null;
  const speedMph = Number.isFinite(gpsSpeedMph) ? Math.round(gpsSpeedMph) : null;
  const currentRoad = roadFromInstruction(activeStep?.instruction);

  // Markers for the navigator: rotate the driver marker to the travel heading so
  // it points the right way (the map is north-up in practice until Head-Up
  // rotation lands, so we apply the absolute heading). Plain map — runs after
  // the early return, so it must not be a hook.
  const markerHeadingRaw = Number.isFinite(headingDeg) ? headingDeg : null;
  // The vehicle marker is screen-aligned HTML. In Head-Up on a VECTOR map the
  // camera rotates the world so the road ahead already points UP, so the marker
  // must rotate by (heading − cameraHeading) to align with the road — which is
  // 0° (straight up) like Google Maps. On RASTER or North-Up the camera doesn't
  // rotate, so the marker uses the absolute heading. This fixes the puck pointing
  // sideways relative to the road in Head-Up mode.
  const isVectorMap = String(renderingType || "").toUpperCase().includes("VECTOR");
  const cameraHeading = headUp && isVectorMap && markerHeadingRaw != null ? markerHeadingRaw : 0;
  const markerHeading =
    markerHeadingRaw != null ? (((markerHeadingRaw - cameraHeading) % 360) + 360) % 360 : null;
  // Snap the displayed vehicle to the nearest point on the DRAWN route polyline
  // (pixel-accurate on curves, unlike the coarse step segments). Only snap when
  // within ~40 m of the line so genuine off-route deviation still shows. Raw GPS
  // continues to drive progress (via `driverPos`) and diagnostics.
  const polySnap = _snapToPolyline(driverPos, route);
  const snappedPos = polySnap && polySnap.distM <= 40 ? polySnap.point : null;
  const navMarkers = (markers || []).map((m) => {
    if (m.type !== "driver" && m.type !== "me") return m;
    return {
      ...m,
      ...(snappedPos ? { lat: snappedPos.lat, lng: snappedPos.lng } : {}),
      ...(markerHeading != null ? { directional: true, heading: markerHeading } : {}),
    };
  });

  // ── Passenger contact (reuses existing endpoints — no new backend) ─────────
  // Masked voice call: driver dials a shared proxy; the customer's real number
  // is never exposed. Works for both app and phone bookings.
  const callPassenger = async () => {
    try {
      const { data } = await api.get(`/driver/rides/${rideId}/call`);
      if (data?.available && data.call_number) {
        toast.success("Connecting through Crystal Dispatch…", { description: data.note });
        window.location.href = `tel:${data.call_number}`;
      } else {
        toast.message(data?.reason || "Calling isn't available right now.");
      }
    } catch {
      toast.error("Couldn't start the call. Please try again.");
    }
  };

  // Templated message: in-app for app bookings, SMS bridge for phone bookings.
  const sendTemplate = async (templateId) => {
    if (sendingId) return;
    setSendingId(templateId);
    try {
      const url = phoneBooking ? `/driver/rides/${rideId}/sms` : `/rides/${rideId}/messages`;
      await api.post(url, { template_id: templateId });
      toast.success(phoneBooking ? "Text sent to passenger" : "Message sent");
      setMsgOpen(false);
    } catch {
      toast.error("Couldn't send. Please try again.");
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black"
      data-testid="navigation-fullscreen"
    >
      {/* Live-following map fills the whole screen */}
      <SmartMap
        markers={navMarkers}
        routes={route && route.length > 1 ? [{ points: route, color: "#002FA7", weight: 6, opacity: 0.9 }] : null}
        follow
        followZoom={18}
        headUp={headUp}
        headingDeg={headingDeg}
        onRenderingType={setRenderingType}
        className="absolute inset-0"
      />

      {/* Heading banner: destination + current road + remaining + ETA clock + exit */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-[#0A0A0A]/92 backdrop-blur-sm text-white px-4 py-3 flex items-center gap-3" data-testid="navigation-header">
        <button
          type="button"
          onClick={onClose}
          className="w-9 h-9 flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors shrink-0"
          data-testid="navigation-exit-btn"
          aria-label="Exit navigation"
        >
          <X className="w-5 h-5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#22C55E] flex items-center gap-1.5">
            <Navigation2 className="w-3 h-3" /> Navigating to
            <span className="bg-[#FBBF24] text-black px-1.5 py-px rounded-sm text-[8px] tracking-widest" data-testid="navigation-beta-badge">BETA</span>
          </div>
          <div className="font-heading font-black text-base truncate" data-testid="navigation-dest-label">
            {destLabel || "Drop-off"}
          </div>
          {currentRoad && (
            <div className="text-[11px] text-white/70 truncate" data-testid="navigation-current-road">
              On {currentRoad}
            </div>
          )}
        </div>
        <div className="text-right shrink-0">
          {etaClock && (
            <div className="font-heading font-black text-lg leading-none" data-testid="navigation-eta-clock">
              ETA {etaClock}
            </div>
          )}
          <div className="text-[11px] text-white/70 mt-0.5" data-testid="navigation-remaining">
            {distanceText}{etaText ? ` · ${etaText}` : ""}
          </div>
          <div
            className="mt-0.5 text-[9px] font-bold tracking-wider text-[#FBBF24]"
            data-testid="navigation-map-rendering-type"
          >
            MAP: {String(renderingType || "…").toUpperCase()} · {NAV_BUILD}
          </div>
        </div>
      </div>

      {/* ARRIVAL banner — highest precedence once the destination is reached. */}
      {arrived && (
        <div className="absolute top-[64px] left-0 right-0 z-10 bg-[#15803D] text-white px-4 py-4 flex items-center gap-3 shadow-xl" data-testid="navigation-arrival-banner">
          <div className="relative shrink-0">
            <span className="absolute inset-0 rounded-full bg-white/30 animate-ping" />
            <Navigation2 className="relative w-10 h-10 fill-white" strokeWidth={2.5} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-black uppercase tracking-widest text-white/90">You've arrived</div>
            <div className="font-heading font-black text-xl leading-tight truncate" data-testid="navigation-arrival-text">
              {destLabel || "Destination"}
            </div>
          </div>
        </div>
      )}

      {/* OFF-ROUTE / REROUTING banner — high precedence (after arrival). */}
      {offRoute && !arrived && (
        <div className="absolute top-[64px] left-0 right-0 z-10 bg-[#DC2626] text-white px-4 py-3 flex items-center gap-3 shadow-lg" data-testid="navigation-offroute-banner">
          <AlertTriangle className="w-7 h-7 shrink-0" strokeWidth={2.5} />
          <div className="min-w-0 flex-1">
            <div className="font-heading font-black text-base leading-tight">Off route</div>
            <div className="text-xs text-white/85 flex items-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" /> Rerouting…
            </div>
          </div>
        </div>
      )}

      {/* Next-turn maneuver banner (escalates to NEAR-TURN within ~100 ft).
          Carries a "then …" preview line so current + upcoming maneuvers show
          together (req #5). */}
      {instruction && !offRoute && !arrived && !nearTurn && (
        <div className="absolute top-[64px] left-0 right-0 z-10 bg-[#002FA7] text-white px-4 py-3 shadow-lg" data-testid="navigation-maneuver-banner">
          <div className="flex items-center gap-3">
            <ManeuverIcon className="w-8 h-8 shrink-0" strokeWidth={2.5} />
            <div className="min-w-0 flex-1">
              <div className="font-heading font-black text-base leading-tight truncate" data-testid="navigation-maneuver-text">
                {instruction}
              </div>
              <div className="text-xs text-white/80 flex items-center gap-2">
                {maneuverDistanceText && <span data-testid="navigation-maneuver-distance">in {maneuverDistanceText}</span>}
                {etaText && <span>· {etaText} to drop-off</span>}
              </div>
            </div>
          </div>
          {thenInstruction && (
            <div className="mt-2 pt-2 border-t border-white/20 flex items-center gap-2 text-white/75" data-testid="navigation-then-maneuver">
              <span className="text-[10px] font-black uppercase tracking-wider shrink-0">Then</span>
              <ThenIcon className="w-4 h-4 shrink-0" strokeWidth={2.5} />
              <span className="text-xs truncate" data-testid="navigation-then-text">{thenInstruction}</span>
            </div>
          )}
        </div>
      )}

      {/* NEAR-TURN banner — within ~100 ft. Larger, high-contrast, pulsing. */}
      {nearTurn && !arrived && (
        <div className="absolute top-[64px] left-0 right-0 z-10 bg-[#16A34A] text-white px-4 py-4 flex items-center gap-4 shadow-xl" data-testid="navigation-near-turn-banner">
          <div className="relative shrink-0">
            <span className="absolute inset-0 rounded-full bg-white/30 animate-ping" />
            <ManeuverIcon className="relative w-11 h-11" strokeWidth={2.75} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-black uppercase tracking-widest text-white/90">
              {nearMeters != null && nearMeters <= 15 ? "Now" : (maneuverDistanceText ? `In ${maneuverDistanceText}` : "Now")}
            </div>
            <div className="font-heading font-black text-xl leading-tight truncate" data-testid="navigation-near-turn-text">
              {instruction}
            </div>
          </div>
        </div>
      )}

      {/* Right-side control stack (below the banner): voice toggle + open native app. */}
      <div className="absolute top-[140px] right-3 z-10 flex flex-col gap-2 items-end">
        <button
          type="button"
          onClick={() => setVoiceOn((v) => {
            const next = !v;
            // Warm up speech inside this user gesture (required by iOS Safari).
            if (next) primeVoice();
            else { try { window.speechSynthesis?.cancel(); } catch { /* ignore */ } }
            return next;
          })}
          className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold shadow-lg transition-colors ${voiceOn ? "bg-[#16A34A] text-white" : "bg-white/95 hover:bg-white text-[#0A0A0A]"}`}
          data-testid="navigation-voice-toggle"
          aria-pressed={voiceOn}
          aria-label={voiceOn ? "Voice on — tap to mute" : "Voice off — tap to enable"}
          title={voiceOn ? "Voice guidance on" : "Voice guidance off"}
        >
          {voiceOn ? <Volume2 className="w-4 h-4" strokeWidth={2.5} /> : <VolumeX className="w-4 h-4" strokeWidth={2.5} />}
          <span data-testid="navigation-voice-label">{voiceOn ? "Voice on" : "Voice"}</span>
        </button>
        {navUrl && (
          <a
            href={navUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 bg-white/95 hover:bg-white text-[#0A0A0A] px-3 py-2 text-xs font-bold shadow-lg transition-colors"
            data-testid="navigation-open-external-btn"
          >
            <ExternalLink className="w-3.5 h-3.5" /> {navAppLabel}
          </a>
        )}
      </div>

      {/* Arrival countdown chip (below banner, left). */}
      {arriveMin != null && (
        <div className="absolute top-[140px] left-3 z-10 flex items-center gap-1.5 bg-[#0A0A0A]/85 backdrop-blur-sm text-white px-3 py-1.5 text-xs font-bold shadow-lg" data-testid="navigation-arrival-countdown">
          <Clock className="w-3.5 h-3.5 text-[#22C55E]" />
          Arriving in {arriveMin} min
        </div>
      )}

      {/* Passenger contact — Call + Message, on the navigation screen so the
          driver never leaves the journey view. */}
      <div className="absolute top-[180px] left-3 z-10 flex gap-2">
        <button
          type="button"
          onClick={callPassenger}
          className="flex items-center gap-1.5 bg-[#16A34A] hover:bg-[#15803D] text-white px-3 py-2 text-xs font-bold shadow-lg transition-colors"
          data-testid="navigation-call-passenger"
          aria-label="Call passenger"
        >
          <Phone className="w-4 h-4" strokeWidth={2.5} /> Call
        </button>
        <button
          type="button"
          onClick={() => setMsgOpen(true)}
          className="flex items-center gap-1.5 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-white px-3 py-2 text-xs font-bold shadow-lg transition-colors"
          data-testid="navigation-message-passenger"
          aria-label="Message passenger"
        >
          <MessageSquare className="w-4 h-4" strokeWidth={2.5} /> Message
        </button>
      </div>

      {/* Quick-message sheet — templated, reuses the existing send endpoints. */}
      {msgOpen && (
        <div className="absolute inset-0 z-[70] flex flex-col justify-end" data-testid="navigation-message-sheet">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            onClick={() => setMsgOpen(false)}
            aria-label="Close messages"
          />
          <div className="relative bg-white p-4 pb-6 shadow-2xl">
            <div className="flex items-center justify-between mb-3">
              <div className="font-heading font-black text-base flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-[#002FA7]" />
                {phoneBooking ? "Text passenger (SMS)" : "Message passenger"}
              </div>
              <button
                type="button"
                onClick={() => setMsgOpen(false)}
                className="w-8 h-8 flex items-center justify-center bg-[#0A0A0A] text-white"
                data-testid="navigation-message-close"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-2">
              {DRIVER_TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  disabled={!!sendingId}
                  onClick={() => sendTemplate(t.id)}
                  className="flex items-center justify-between gap-2 border-2 border-[#0A0A0A] px-3 py-2.5 text-sm font-bold text-left hover:bg-[#F3F4F6] disabled:opacity-50 transition-colors"
                  data-testid={`navigation-msg-template-${t.id}`}
                >
                  <span>{t.body}</span>
                  {sendingId === t.id
                    ? <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                    : <Send className="w-4 h-4 text-[#002FA7] shrink-0" />}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Bottom-left control stack: speed pill + orientation toggle. */}
      <div className="absolute bottom-3 left-3 z-10 flex flex-col gap-2 items-start">
        <div className="flex items-center gap-2 bg-white/95 text-[#0A0A0A] px-3 py-2 shadow-lg" data-testid="navigation-speed">
          <Gauge className="w-4 h-4 text-[#002FA7]" strokeWidth={2.5} />
          <span className="font-heading font-black text-base leading-none" data-testid="navigation-speed-value">
            {speedMph != null ? speedMph : "—"}
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wider text-[#6B7280]">mph</span>
        </div>
        <button
          type="button"
          onClick={() => setHeadUp((v) => !v)}
          className="flex items-center gap-2 bg-white/95 hover:bg-white text-[#0A0A0A] px-3 py-2 text-xs font-bold shadow-lg transition-colors"
          data-testid="map-orientation-toggle"
          aria-pressed={headUp}
          aria-label={headUp ? "Head Up — tap for North Up" : "North Up — tap for Head Up"}
          title={headUp ? "Head Up (follows your heading)" : "North Up (map fixed to north)"}
        >
          {headUp
            ? <Navigation2 className="w-4 h-4 text-[#002FA7]" strokeWidth={2.5} />
            : <Compass className="w-4 h-4 text-[#0A0A0A]" strokeWidth={2.5} />}
          <span data-testid="map-orientation-label">{headUp ? "Head Up" : "North Up"}</span>
        </button>
        <button
          type="button"
          onClick={() => setDebugOn((v) => !v)}
          className={`flex items-center gap-2 px-3 py-2 text-xs font-bold shadow-lg transition-colors ${debugOn ? "bg-[#0A0A0A] text-[#22C55E]" : "bg-white/95 hover:bg-white text-[#0A0A0A]"}`}
          data-testid="navigation-debug-toggle"
          aria-pressed={debugOn}
          title="Navigation debug overlay"
        >
          <Bug className="w-4 h-4" strokeWidth={2.5} />
          <span>Debug</span>
        </button>
        {headUpUnavailable && (
          <div
            className="bg-[#0A0A0A]/85 text-white px-2.5 py-1 text-[10px] font-semibold shadow-lg max-w-[200px]"
            data-testid="map-orientation-note"
          >
            Heading unavailable — map stays north-facing until movement is detected.
          </div>
        )}
      </div>

      {/* NAVIGATION DEBUG OVERLAY (req #10) — client-side nav telemetry. */}
      {debugOn && (
        <div
          className="absolute bottom-3 right-3 z-20 bg-[#0A0A0A]/92 text-white px-3 py-2.5 shadow-2xl font-mono-tab text-[10px] leading-relaxed max-w-[230px] space-y-0.5"
          data-testid="navigation-debug-overlay"
        >
          <div className="text-[#22C55E] font-bold tracking-wider mb-1">NAV DEBUG · {NAV_BUILD}</div>
          <div data-testid="dbg-mode">mode: {headUp ? "HEAD-UP" : "NORTH-UP"} · {String(renderingType || "…").toUpperCase()}{offRoute ? " · OFF-ROUTE" : arrived ? " · ARRIVED" : " · ON-ROUTE"}</div>
          <div data-testid="dbg-accuracy">gps acc: {gpsAccuracy != null ? `${gpsAccuracy} m` : "—"}</div>
          <div data-testid="dbg-heading">heading: {Number.isFinite(headingDeg) ? `${Math.round(headingDeg)}°` : "—"} ({headingSource})</div>
          <div data-testid="dbg-step">step: {tbt.stepIndex != null ? `${tbt.stepIndex}` : "—"} / {tbt.total || 0} · {tbt.segmentId || "—"}</div>
          <div data-testid="dbg-dist">to maneuver: {Number.isFinite(maneuverMeters) ? `${Math.round(maneuverMeters)} m` : "—"}</div>
          <div data-testid="dbg-progress">progress: {tbt.progressPct != null ? `${tbt.progressPct}%` : "—"}</div>
          <div data-testid="dbg-fix">gps fix: {lastFixAt ? `${Math.round((Date.now() - lastFixAt) / 1000)}s ago` : "—"}</div>
          <div data-testid="dbg-heartbeat">heartbeat: {lastHeartbeatAt ? `${Math.round((Date.now() - lastHeartbeatAt) / 1000)}s ago` : "—"}</div>
          <div data-testid="dbg-snap">snap: {snappedPos ? `${snappedPos.lat.toFixed(5)},${snappedPos.lng.toFixed(5)} (Δ${polySnap ? Math.round(polySnap.distM) : "—"}m)` : (polySnap ? `OFF-ROUTE Δ${Math.round(polySnap.distM)}m` : "—")}</div>
          <div data-testid="dbg-voice">voice: {voiceOn ? (voice.lastSpoken ? `#${voice.lastSpoken.n} "${voice.lastSpoken.text.slice(0, 22)}"` : "on · none yet") : "off"}{voice.supported ? "" : " · UNSUPPORTED"}</div>
        </div>
      )}

      {/* Compact LIVE fare overlay — bottom-right, expands into the full meter. */}
      <FloatingFarePill
        rideId={rideId}
        startedAt={startedAt}
        fallbackEstimate={fallbackEstimate}
      />
    </div>
  );
}

export default NavigationFullScreen;
